import React, { FunctionComponent } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  MenuItem,
  Grid,
  Button as MuiButton,
  ButtonGroup,
  IconButton,
  FormControlLabel,
  Checkbox,
  SvgIcon,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import {
  Button,
  CustomDatePicker,
  CustomInput,
  CustomSelect,
} from "../../components";
import { CustomBreadCrumbs } from "../../components/CustomBreadcrumbs";
import { CustomTextArea } from "../../components/CustomTextArea";
import { Save, Search, Add, Remove } from "@mui/icons-material";
import { User } from "../../model/User";
import { CustomSnackbar } from "../../components/CustomSnackbar";
import { LeadInfoDialog } from "../../components/LeadInfoDialog";
import { useAddLead } from "./hook";
import { CustomAutocomplete } from "../../components/CustomAutocomplete";
import { PossibleMatchesDialog } from "../../components/PossibleMatchesDialog";
import Icon from "@mdi/react";
import { mdiClockOutline } from "@mdi/js";

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&:before": {
    display: "none",
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={
      <ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem", color: "white" }} />
    }
    {...props}
  />
))(({ theme }) => ({
  backgroundColor: "#ed3e3e",
  color: "white",
  flexDirection: "row-reverse",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(90deg)",
  },
  "& .MuiAccordionSummary-content": {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: "1px solid rgba(0, 0, 0, .125)",
  backgroundColor: "#f7d5d7",
}));

const SectionTitle: FunctionComponent<{ title: string }> = (
  props
): JSX.Element => {
  return (
    <Box
      sx={{ p: 2, backgroundColor: "#F5F5F5", borderRadius: 4, mt: 2, mb: 2 }}
    >
      <Typography fontSize={18} fontWeight="600" color="#1BA0E9">
        {props.title}
      </Typography>
    </Box>
  );
};

export const AddLead = (): JSX.Element => {
  const {
    leadState,
    possibleMatches,
    addPhoneNumber,
    snackbarState,
    handleSnackbarClose,
    dealerSources,
    deletePhoneNumber,
    handleDealerInfoClose,
    formFields,
    handleDealerInfoOpen,
    isPossibleMatchesDialogOpen,
    handlePossibleMatchesClose,
    handlePossibleMatchesOpen,
    onHandlePhonenumberChange,
    onChangeDealer,
    onChangeFormField,
    onChangeFormFieldSelect,
    onFetchDealerMsg,
    softMsgColor,
    dealerMsg,
    isDealerInfoDialogOpen,
    dealer,
    onChange,
    onChangeAutocomplete,
    source,
    phoneNumbers,
    products,
    dropdownApptTimeRange,
    onChangeSelect,
    onAddLead,
    getDealerInfo,
    dealerMsgColor,
    onChangeDate,
    leadStatuses,
  } = useAddLead();

  const user: User = new User();

  const ImportantNotice: FunctionComponent = () => {
    return dealerMsg ? (
      <Box bgcolor={dealerMsgColor} width="100%" mt={4}>
        <Accordion>
          <AccordionSummary
            sx={{ bgcolor: dealerMsgColor }}
            aria-controls="panel1d-content"
            id="panel1d-header"
          >
            <Typography>Important Notice</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ bgcolor: softMsgColor }}>
            <div dangerouslySetInnerHTML={{ __html: dealerMsg }} />
          </AccordionDetails>
        </Accordion>
      </Box>
    ) : null;
  };

  return (
    <>
      <Grid xs={12} sx={{ mb: 2 }}>
        <CustomBreadCrumbs path="Home/Add Lead" />
      </Grid>
      <Grid
        container
        columns={{ xs: 12, sm: 10, md: 10, lg: 12 }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Grid xs={12} sm={5} md={5} lg={8}>
          <Typography
            textAlign={{ xs: "center", sm: "start" }}
            variant="h4"
            sx={{ color: "#1BA0E9", fontWeight: 600, fontSize: 25 }}
          >
            Add Lead
          </Typography>
        </Grid>
        <ImportantNotice />
        <Box width="100%" display="flex" alignItems="center" gap={2} mt={4}>
          <CustomSelect
            name="dealer"
            value={dealer}
            onChange={onChangeDealer}
            label="Dealer"
            labelAdornment={<span style={{ color: "red" }}>*</span>}
          >
            {user.Accounts.map((val) => {
              return (
                <MenuItem value={val.dealerId}>{val.companyName}</MenuItem>
              );
            })}
          </CustomSelect>
          <ButtonGroup
            disableElevation
            variant="contained"
            aria-label="Disabled elevation buttons"
            sx={{ marginTop: 3 }}
          >
            <MuiButton
              sx={{
                backgroundColor: "#1BA0E9",
                height: 35,
                textTransform: "none",
                fontFamily: `"Lato", "Roboto", "Helvetica", "Arial"`,
              }}
              onClick={getDealerInfo}
            >
              Find Dealer
            </MuiButton>
            <MuiButton
              aria-haspopup="menu"
              sx={{ backgroundColor: "#1BA0E9", height: 35 }}
              disabled={!dealer}
              onClick={handleDealerInfoOpen}
            >
              <Search />
            </MuiButton>
          </ButtonGroup>
        </Box>
        <FormControlLabel control={<Checkbox />} label="Send email to dealer" />
      </Grid>
      <Box display="flex" alignItems="center" justifyContent="center">
        <Card sx={{ borderRadius: 4, width: "100%" }}>
          <CardContent>
            <SectionTitle title="Lead Information" />
            <CustomAutocomplete
              disabled={false}
              options={dealerSources}
              name="dialedTollFree"
              label={{
                text: "Lead Source",
              }}
              labelAdorment={<span style={{ color: "red" }}>*</span>}
              value={source}
              onChange={(event, val) => onChangeAutocomplete(event, val, "")}
            />

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { sm: "1fr 1fr 1fr" },
                gap: 2,
                mt: 4,
              }}
            >
              <CustomInput
                name="firstname"
                value={leadState.firstname}
                height={35}
                label={{ text: "First Name" }}
                onChange={onChange}
              />
              <CustomInput
                name="lastname"
                value={leadState.lastname}
                height={35}
                onChange={onChange}
                label={{ text: "Last Name" }}
              />
              <CustomInput
                name="address"
                value={leadState.address}
                height={35}
                onChange={onChange}
                label={{ text: "Address" }}
              />
              <CustomInput
                name="city"
                value={leadState.city}
                height={35}
                onChange={onChange}
                label={{ text: "City" }}
              />
              <CustomInput
                name="state"
                value={leadState.state}
                onChange={onChange}
                height={35}
                label={{ text: "State" }}
              />
              <CustomInput
                name="zip"
                value={leadState.zip}
                onChange={onChange}
                height={35}
                label={{ text: "Zip" }}
                labelAdornment={<span style={{ color: "red" }}>*</span>}
              />
              <CustomInput
                name="email"
                value={leadState.email}
                onChange={onChange}
                height={35}
                label={{ text: "Email" }}
              />
              <CustomInput
                name="email2"
                value={leadState.email2}
                onChange={onChange}
                height={35}
                label={{ text: "Email 2" }}
              />
              <CustomSelect
                name="product"
                value={leadState.product}
                label="Product"
                onChange={onChangeSelect}
              >
                {products.map((val: any) => {
                  return <MenuItem value={val.id}>{val.description}</MenuItem>;
                })}
              </CustomSelect>
              <CustomSelect
                name="status"
                value={leadState.status}
                label="Status"
                labelAdornment={<span style={{ color: "red" }}>*</span>}
                onChange={onChangeSelect}
              >
                {leadStatuses.map((val) => {
                  return <MenuItem value={val.id}>{val.description}</MenuItem>;
                })}
              </CustomSelect>
              <CustomDatePicker
                value={leadState.apptDt}
                width="100%"
                label={{ text: "Appointment Date" }}
                onChange={onChangeDate}
              />
              <CustomSelect
                MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
                width="100%"
                name="time"
                value={leadState.time}
                label="Appointment Time"
                StartIcon={
                  <SvgIcon>
                    <Icon path={mdiClockOutline} />
                  </SvgIcon>
                }
                onChange={onChangeSelect}
              >
                {dropdownApptTimeRange.map((item, index) => {
                  return (
                    <MenuItem key={index} value={item.description}>
                      {item.description}
                    </MenuItem>
                  );
                })}
              </CustomSelect>
            </Box>
            {/* <SectionTitle title={`Add Phone numbers (${phoneNumbers.length} / 3)`} /> */}
            <Box display="flex" gap={2} my={3}>
              <Stack direction="column" spacing={2}>
                {phoneNumbers.map((_, index) => {
                  return (
                    <CustomInput
                      name={index.toString()}
                      value={phoneNumbers[index]}
                      labelAdornment={<span style={{ color: "red" }}>*</span>}
                      onChange={onHandlePhonenumberChange}
                      height={35}
                      label={{ text: "Phone Number" }}
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                    />
                  );
                })}
              </Stack>
              <Box mt={3} display="flex" alignItems="flex-start" gap={1}>
                <IconButton
                  onClick={addPhoneNumber}
                  sx={{
                    borderRadius: 2,
                    bgcolor: "#1BA0E9",
                    color: "white",
                    ":hover": { bgcolor: "#1BA0E9", color: "white" },
                  }}
                >
                  <Add />
                </IconButton>
                <IconButton
                  onClick={deletePhoneNumber}
                  sx={{
                    borderRadius: 2,
                    bgcolor: "#ed3e3e",
                    color: "white",
                    ":hover": { bgcolor: "#ed3e3e", color: "white" },
                  }}
                >
                  <Remove />
                </IconButton>
              </Box>
            </Box>
            <Box mt={2}>
              <CustomTextArea
                label={{ text: "Notes" }}
                name="notes"
                value={leadState.notes}
                onChange={onChange}
                width="100%"
                maxLength={500}
              />
            </Box>
            <SectionTitle title="Additional Fields" />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { sm: "1fr 1fr 1fr" },
                gap: 2,
                mt: 4,
              }}
            >
              {formFields.map((val, index) => {
                if (val.fieldType === "D") {
                  return (
                    <CustomSelect
                      key={index}
                      name={val.fieldName}
                      value={val.selectedValue}
                      label={val.fieldName}
                      labelAdornment={
                        val.required ? (
                          <span style={{ color: "red" }}>*</span>
                        ) : null
                      }
                      onChange={(event) =>
                        onChangeFormFieldSelect(event, index)
                      }
                    >
                      {val.dropdownValues.map((val: any) => {
                        return (
                          <MenuItem value={val.id}>{val.description}</MenuItem>
                        );
                      })}
                    </CustomSelect>
                  );
                }

                return (
                  <CustomInput
                    key={index}
                    name={val.fieldName}
                    value={val.selectedValue}
                    labelAdornment={
                      val.required ? (
                        <span style={{ color: "red" }}>*</span>
                      ) : null
                    }
                    onChange={(event) => onChangeFormField(event, index)}
                    height={35}
                    label={{ text: val.fieldName }}
                  />
                );
              })}
            </Box>

            <Box
              sx={{
                width: "100%",
                borderTop: "1px solid rgba(0, 0, 0, 0.12)",
                mt: 6,
                p: 2,
              }}
            >
              <Stack direction="row" justifyContent="flex-end">
                <Stack direction="row" spacing={2}>
                  <Button
                    height={40}
                    startIcon={<Save />}
                    label="Save"
                    onClick={onAddLead}
                  />
                </Stack>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </Box>
      <CustomSnackbar
        open={snackbarState.open}
        variant="secondary"
        level={snackbarState.level as any}
        message={snackbarState.title}
        action={snackbarState.action}
        onClose={handleSnackbarClose}
      />
      <LeadInfoDialog
        id={dealer}
        open={isDealerInfoDialogOpen}
        onClose={handleDealerInfoClose}
      />
      <PossibleMatchesDialog
        open={isPossibleMatchesDialogOpen}
        onClose={handlePossibleMatchesClose}
        matches={possibleMatches.map((val) => val.id)}
      />
    </>
  );
};
